// BUG-81 — Sane ErrorBoundary
//
// Previous implementation rendered `null` on any caught error (blank page),
// auto-reloaded after 500 ms, called localStorage.clear() + redirected to "/"
// after 2 errors in 30 s, AND monkey-patched console.error to trigger
// further reloads on messages containing "chunk" / "failed" / "network".
// Net effect: a single render bug anywhere in the tree silently blanked the
// entire app, hid the cause from devs, and eventually logged users out.
//
// This version: show a real fallback UI (error message + stack + manual
// buttons). No auto-reload, no auto-logout, no console-error monkey-patch.
//
// Note on styling: this is the one place STYLE.md tokens are intentionally
// not used — when the boundary is rendering, app CSS may have failed to
// load, so we use inline styles with raw hex so the fallback always renders.

import React, { Component } from "react";
import PropTypes from "prop-types";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Caught render error:", error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message =
      this.state.error?.message ||
      (this.state.error ? String(this.state.error) : "Unknown error");
    const stack = this.state.error?.stack || "";

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#F8FAFC",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 640,
            width: "100%",
            background: "white",
            border: "1px solid #FECACA",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            padding: 32,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                background: "#FEE2E2",
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                fontWeight: 700,
              }}
            >
              !
            </div>
            <h2 style={{ margin: 0, fontSize: 18, color: "#0F172A" }}>
              Something went wrong
            </h2>
          </div>
          <p style={{ color: "#475569", fontSize: 14, marginBottom: 16 }}>
            The page hit an unexpected error and couldn&rsquo;t render. The
            details below help engineering diagnose the cause.
          </p>
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: 8,
              padding: 12,
              color: "#991B1B",
              fontSize: 12,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              marginBottom: 16,
              wordBreak: "break-word",
            }}
          >
            {message}
          </div>
          {stack ? (
            <details style={{ marginBottom: 16 }}>
              <summary
                style={{ cursor: "pointer", color: "#64748B", fontSize: 12 }}
              >
                Stack trace
              </summary>
              <pre
                style={{
                  background: "#F1F5F9",
                  padding: 12,
                  borderRadius: 8,
                  fontSize: 11,
                  overflow: "auto",
                  maxHeight: 240,
                  color: "#334155",
                  marginTop: 8,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {stack}
              </pre>
            </details>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                background: "#16A34A",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              style={{
                background: "white",
                color: "#334155",
                border: "1px solid #CBD5E1",
                padding: "8px 16px",
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Go to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
